import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { trackPageview } from "@/lib/tracker";
import WebFrame from "@/components/layout/WebFrame";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import DoctorDetail from "@/pages/DoctorDetail";
import Cart from "@/pages/Cart";
import Orders from "@/pages/Orders";
import Consultation from "@/pages/Consultation";
import ChangePassword from "@/pages/account/ChangePassword";
import DoshaQuiz from "@/pages/wellness/DoshaQuiz";
import DoshaProfile from "@/pages/wellness/DoshaProfile";
import AiGuide from "@/pages/wellness/AiGuide";
import PatientDashboard from "@/pages/patient/PatientDashboard";
import HealthProfile from "@/pages/patient/HealthProfile";
import Vitals from "@/pages/patient/Vitals";
import Tests from "@/pages/patient/Tests";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import RegisterDoctor from "@/pages/auth/RegisterDoctor";
import PendingGate from "@/pages/doctor/PendingGate";
import DoctorDashboard from "@/pages/doctor/Dashboard";
import DoctorChats from "@/pages/doctor/Chats";
import DoctorAppointments from "@/pages/doctor/Appointments";
import VaidyaTriage from "@/pages/doctor/Triage";
import DoctorPatientView from "@/pages/doctor/PatientView";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/Overview";
import AdminProducts from "@/pages/admin/Products";
import AdminDoctors from "@/pages/admin/Doctors";
import AdminOrders from "@/pages/admin/Orders";
import AdminUsers from "@/pages/admin/Users";
import AdminQuiz from "@/pages/admin/AdminQuiz";

function RequireAuth({ role, children }) {
    const { user, ready } = useAuth();
    const loc = useLocation();
    if (!ready) return null;
    if (!user) return <Navigate to="/auth/login" state={{ from: loc }} replace />;
    if (role && user.role !== role) return <Navigate to="/" replace />;
    return children;
}

function DoctorGate({ children }) {
    const { user, ready } = useAuth();
    if (!ready) return null;
    if (!user) return <Navigate to="/auth/login" replace />;
    if (user.role !== "doctor") return <Navigate to="/" replace />;
    if (user.doctor_status !== "approved")
        return <Navigate to="/doctor/pending" replace />;
    return children;
}

function DoctorOrAdminGate({ children }) {
    const { user, ready } = useAuth();
    if (!ready) return null;
    if (!user) return <Navigate to="/auth/login" replace />;
    if (user.role === "admin") return children;
    if (user.role === "doctor" && user.doctor_status === "approved") return children;
    return <Navigate to="/" replace />;
}

function Framed({ children, hideChrome }) {
    return <WebFrame hideChrome={hideChrome}>{children}</WebFrame>;
}

function VisitTracker() {
    const loc = useLocation();
    useEffect(() => {
        trackPageview(loc.pathname);
    }, [loc.pathname]);
    return null;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <CartProvider>
                        <VisitTracker />
                        <Routes>
                            <Route path="/" element={<Framed><Home /></Framed>} />
                            <Route path="/shop" element={<Framed><Shop /></Framed>} />
                            <Route path="/wellness/quiz" element={<Framed><DoshaQuiz /></Framed>} />
                            <Route path="/wellness/guide" element={<Framed><AiGuide /></Framed>} />
                            <Route
                                path="/wellness/profile"
                                element={
                                    <RequireAuth>
                                        <Framed><DoshaProfile /></Framed>
                                    </RequireAuth>
                                }
                            />
                            <Route path="/product/:id" element={<Framed><ProductDetail /></Framed>} />
                            <Route path="/doctor/:id" element={<Framed><DoctorDetail /></Framed>} />
                            <Route path="/cart" element={<Framed><Cart /></Framed>} />

                            <Route
                                path="/orders"
                                element={
                                    <RequireAuth>
                                        <Framed><Orders /></Framed>
                                    </RequireAuth>
                                }
                            />
                            <Route
                                path="/consultation/:id"
                                element={
                                    <RequireAuth>
                                        <Framed><Consultation /></Framed>
                                    </RequireAuth>
                                }
                            />

                            <Route
                                path="/account/password"
                                element={
                                    <RequireAuth>
                                        <Framed><ChangePassword /></Framed>
                                    </RequireAuth>
                                }
                            />

                            <Route
                                path="/dashboard"
                                element={
                                    <RequireAuth>
                                        <Framed><PatientDashboard /></Framed>
                                    </RequireAuth>
                                }
                            />

                            <Route
                                path="/patient/health-profile"
                                element={
                                    <RequireAuth role="customer">
                                        <Framed><HealthProfile /></Framed>
                                    </RequireAuth>
                                }
                            />

                            <Route
                                path="/patient/vitals"
                                element={
                                    <RequireAuth role="customer">
                                        <Framed><Vitals /></Framed>
                                    </RequireAuth>
                                }
                            />

                            <Route
                                path="/patient/tests"
                                element={
                                    <RequireAuth role="customer">
                                        <Framed><Tests /></Framed>
                                    </RequireAuth>
                                }
                            />

                            {/* Auth */}
                            <Route path="/auth/login" element={<Framed><Login /></Framed>} />
                            <Route path="/auth/register" element={<Framed><Register /></Framed>} />
                            <Route
                                path="/auth/register-doctor"
                                element={<Framed><RegisterDoctor /></Framed>}
                            />

                            {/* Doctor */}
                            <Route
                                path="/doctor/pending"
                                element={
                                    <RequireAuth role="doctor">
                                        <Framed><PendingGate /></Framed>
                                    </RequireAuth>
                                }
                            />
                            <Route
                                path="/doctor/dashboard"
                                element={
                                    <DoctorGate>
                                        <Framed><DoctorDashboard /></Framed>
                                    </DoctorGate>
                                }
                            />
                            <Route
                                path="/doctor/chats"
                                element={
                                    <DoctorGate>
                                        <Framed><DoctorChats /></Framed>
                                    </DoctorGate>
                                }
                            />
                            <Route
                                path="/doctor/appointments"
                                element={
                                    <DoctorGate>
                                        <Framed><DoctorAppointments /></Framed>
                                    </DoctorGate>
                                }
                            />
                            <Route
                                path="/doctor/patient/:userId"
                                element={
                                    <DoctorGate>
                                        <Framed><DoctorPatientView /></Framed>
                                    </DoctorGate>
                                }
                            />
                            <Route
                                path="/doctor/triage"
                                element={
                                    <DoctorOrAdminGate>
                                        <Framed><VaidyaTriage /></Framed>
                                    </DoctorOrAdminGate>
                                }
                            />

                            {/* Admin */}
                            <Route
                                path="/admin"
                                element={
                                    <RequireAuth role="admin">
                                        <Framed><AdminLayout /></Framed>
                                    </RequireAuth>
                                }
                            >
                                <Route
                                    index
                                    element={<Navigate to="/admin/overview" replace />}
                                />
                                <Route path="overview" element={<AdminOverview />} />
                                <Route path="products" element={<AdminProducts />} />
                                <Route path="doctors" element={<AdminDoctors />} />
                                <Route path="orders" element={<AdminOrders />} />
                                <Route path="users" element={<AdminUsers />} />
                                <Route path="quiz" element={<AdminQuiz />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </CartProvider>
                </AuthProvider>
            </BrowserRouter>
            <Toaster position="top-center" richColors closeButton theme="light" />
        </div>
    );
}

export default App;
